import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import SEO from '../components/SEO';
import { CreditBar } from '../components/CreditDisplay';
import RunAgentModal from '../components/RunAgentModal';
import { useCommand } from '../context/CommandContext';

import {
  ArrowRight,
  CaretRight,
  ChatText,
  Clock,
  Compass,
  FileText,
  GearSix,
  Headphones,
  Lightbulb,
  Lightning,
  Megaphone,
  Microscope,
  Play,
  Sparkle,
  SquaresFour,
  TrendUp,
  Wallet,
  Wrench
} from '@phosphor-icons/react';
const OUTCOME_COLORS = {
  'Increase Sales': { bg: 'rgba(16,185,129,0.10)', text: '#10B981' },
  'Save Time': { bg: 'rgba(59,130,246,0.10)', text: '#3B82F6' },
  'Reduce Admin': { bg: 'rgba(168,85,247,0.10)', text: '#A855F7' },
  'Customer Support': { bg: 'rgba(236,72,153,0.10)', text: '#EC4899' },
  'Marketing': { bg: 'rgba(249,115,22,0.10)', text: '#F97316' },
  'Finance & Admin': { bg: 'rgba(234,179,8,0.10)', text: '#EAB308' },
  'Operations': { bg: 'rgba(99,102,241,0.10)', text: '#6366F1' },
  'Research & Analysis': { bg: 'rgba(20,184,166,0.10)', text: '#14B8A6' },
};

const OUTCOME_ICONS = {
  'Increase Sales': TrendUp,
  'Save Time': Clock,
  'Reduce Admin': FileText,
  'Customer Support': Headphones,
  'Marketing': Megaphone,
  'Finance & Admin': Wallet,
  'Operations': GearSix,
  'Research & Analysis': Microscope,
};

const QUICK_ACTIONS = [
  {
    icon: Play,
    title: 'Run an Agent',
    description: 'Execute your installed agents',
    path: '/partner-pro/ai-workforce/agents',
    color: '#10B981',
  },
  {
    icon: Compass,
    title: 'Explore Automations',
    description: 'Browse ready-to-install automations',
    path: '/automations',
    color: '#EC4899',
  },
  {
    icon: Wrench,
    title: 'Build an Agent',
    description: 'Create a custom AI agent',
    action: 'open_command',
    color: '#6366F1',
  },
  {
    icon: Lightbulb,
    title: 'Find AI Ideas',
    description: 'Discover automation opportunities',
    path: '/assessments',
    color: '#F97316',
  },
];

export default function WorkspaceLite() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { plan } = usePlan();
  const { openCommand } = useCommand();
  const [agents, setAgents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [agentToRun, setAgentToRun] = useState(null);

  useEffect(() => {
    fetchData();
    fetchAutomations();
    fetchUserInfo();
  }, [activeOrganization]);

  const fetchUserInfo = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUserName(data.name?.split(' ')[0] || '');
        if (data.credits) setCredits(data.credits);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchData = async () => {
    if (!activeOrganization?.organization_id) return;
    try {
      setLoading(true);
      const [agentsRes, runsRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/agents`, {}, activeOrganization.organization_id),
        fetchWithOrg(`${BACKEND_URL}/api/agent/all/runs?limit=5&page=1`, {}, activeOrganization.organization_id),
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents((data.agents || data || []).slice(0, 6));
      }
      if (runsRes.ok) {
        const data = await runsRes.json();
        setRuns((data.runs || data || []).slice(0, 5));
      }
    } catch (e) {
      console.error('WorkspaceLite fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/automations?limit=100`);
      if (!res.ok) return;
      const data = await res.json();
      const shuffled = (data.templates || []).sort(() => 0.5 - Math.random());
      setAutomations(shuffled.slice(0, 6));
    } catch (e) { /* ignore */ }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <>
      <SEO title="Workspace | Man & Machine" />
      <div className="min-h-screen p-6 lg:p-8 space-y-8" data-testid="workspace-lite-page">
        {/* Greeting + Credits */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {userName ? `Welcome back, ${userName}` : 'Welcome back'}
            </h1>
            <p className="text-sm text-[#8b8fa3]">What would you like to do today?</p>
          </div>
          {credits && (
            <div className="lg:w-72 shrink-0">
              <CreditBar credits={credits} />
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <section data-testid="workspace-lite-quick-actions">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.title}
                  data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => action.action === 'open_command' ? openCommand() : navigate(action.path)}
                  className="group text-left p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(255,255,255,0.12)] hover:bg-[rgba(255,255,255,0.04)] transition-all duration-200"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${action.color}15` }}
                  >
                    <Icon size={18} style={{ color: action.color }} />
                  </div>
                  <h3 className="text-white text-sm font-semibold mb-0.5 group-hover:text-[#EC4899] transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-[#555] text-xs">{action.description}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Your Agents */}
        <section data-testid="workspace-lite-your-agents">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <SquaresFour size={16} className="text-[#6366F1]" />
              Your Agents
            </h2>
            <Link
              to="/partner-pro/ai-workforce/agents"
              className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1"
            >
              View all <CaretRight size={12} />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : agents.length === 0 ? (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-8 text-center" data-testid="workspace-lite-no-agents">
              <Lightning size={24} className="text-[#555] mx-auto mb-3" />
              <p className="text-[#8b8fa3] text-sm mb-1">No agents yet</p>
              <p className="text-[#555] text-xs mb-4">Install one from the gallery or build your own</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => navigate('/automations')}
                  className="text-xs px-4 py-2 rounded-lg bg-gradient-to-r from-[#EC4899] to-[#6366F1] text-white font-medium hover:opacity-90 transition-opacity"
                >
                  Browse Automations
                </button>
                <button
                  onClick={() => openCommand()}
                  className="text-xs px-4 py-2 rounded-lg border border-[rgba(255,255,255,0.1)] text-[#8b8fa3] hover:text-white transition-colors"
                  data-testid="workspace-build-agent-btn"
                >
                  Build Agent
                </button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {agents.map((agent) => (
                <div
                  key={agent.agent_id || agent.id}
                  className="group rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-4 hover:border-[rgba(255,255,255,0.12)] transition-all"
                  data-testid={`agent-card-${agent.agent_id || agent.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.12)] flex items-center justify-center">
                        <ChatText size={14} className="text-[#6366F1]" />
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium line-clamp-1">{agent.name}</h3>
                        <span className={`text-[10px] ${agent.status === 'active' ? 'text-green-400' : 'text-[#555]'}`}>
                          {agent.status || 'draft'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[#555] text-xs line-clamp-2 mb-3">
                    {agent.description || agent.short_description || 'No description'}
                  </p>
                  <button
                    onClick={() => setAgentToRun(agent)}
                    data-testid={`run-agent-workspace-${agent.agent_id || agent.id}`}
                    className="w-full text-xs py-1.5 rounded-lg border border-[rgba(99,102,241,0.3)] text-[#6366F1] hover:bg-[rgba(99,102,241,0.08)] transition-colors font-medium"
                  >
                    Run Agent
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recommended Automations */}
        <section data-testid="workspace-lite-recommended">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Sparkle size={16} className="text-[#EC4899]" />
              Recommended Automations
            </h2>
            <Link
              to="/automations"
              className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1"
            >
              View all <CaretRight size={12} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {automations.map((t) => {
              const color = OUTCOME_COLORS[t.outcome_category] || OUTCOME_COLORS['Operations'];
              const OutIcon = OUTCOME_ICONS[t.outcome_category] || GearSix;
              return (
                <Link
                  key={t.slug}
                  to={`/automations/${t.slug}`}
                  data-testid={`recommended-automation-${t.slug}`}
                  className="group block"
                >
                  <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-4 hover:border-[rgba(255,255,255,0.12)] transition-all h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center shrink-0"
                        style={{ backgroundColor: color.bg }}
                      >
                        <OutIcon size={12} style={{ color: color.text }} />
                      </div>
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: color.bg, color: color.text }}
                      >
                        {t.outcome_category}
                      </span>
                    </div>
                    <h3 className="text-white text-sm font-medium mb-1 group-hover:text-[#EC4899] transition-colors line-clamp-1">
                      {t.name}
                    </h3>
                    <p className="text-[#555] text-xs line-clamp-2">
                      {t.short_description || t.description?.replace(/\*\*/g, '').substring(0, 80)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Recent Runs */}
        <section data-testid="workspace-lite-recent-runs">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Clock size={16} className="text-[#14B8A6]" />
              Recent Runs
            </h2>
            <Link
              to="/partner-pro/ai-workforce/run-history"
              className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1"
            >
              View all <CaretRight size={12} />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#14B8A6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : runs.length === 0 ? (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-6 text-center" data-testid="workspace-lite-no-runs">
              <Clock size={20} className="text-[#555] mx-auto mb-2" />
              <p className="text-[#8b8fa3] text-sm">No runs yet</p>
              <p className="text-[#555] text-xs">Run an agent to see results here</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] overflow-hidden">
              {runs.map((run, idx) => (
                <div
                  key={run.run_id || run.id || idx}
                  data-testid={`recent-run-${run.run_id || run.id || idx}`}
                  className={`flex items-center justify-between p-3 hover:bg-[rgba(255,255,255,0.02)] transition-colors ${idx > 0 ? 'border-t border-[rgba(255,255,255,0.04)]' : ''}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      run.status === 'completed' ? 'bg-green-400' :
                      run.status === 'failed' ? 'bg-red-400' :
                      run.status === 'running' ? 'bg-blue-400 animate-pulse' :
                      'bg-[#555]'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">
                        {run.agent_name || run.agent_id || 'Agent Run'}
                      </p>
                      <p className="text-[#555] text-[10px]">
                        {run.status || 'unknown'} {run.created_at ? `· ${formatTimeAgo(run.created_at)}` : ''}
                      </p>
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-[#333] shrink-0" />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Run Agent Modal */}
      {agentToRun && (
        <RunAgentModal
          agent={{ ...agentToRun, id: agentToRun.agent_id || agentToRun.id }}
          isOpen={!!agentToRun}
          onClose={() => setAgentToRun(null)}
          onSuccess={(sessionId) => { console.log('Session completed:', sessionId); }}
        />
      )}
    </>
  );
}
